/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { modifier } from 'ember-modifier';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import {
  type IMentionable,
  encodeMentions,
} from 'uplisting-frontend/utils/team-mentions';

const STICK_TO_BOTTOM_THRESHOLD = 24;

interface IArgs {
  resourceType: string;
  resourceId: string;
  conversation?: ModelRegistry['team-conversation'];
  messages: ModelRegistry['team-message'][];
  mentionables?: IMentionable[];
  isLoading?: boolean;
  onPosted(message: ModelRegistry['team-message']): void;
}

interface TeamThreadSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiTeamThreadComponent extends Component<TeamThreadSignature> {
  @service notifications!: Services['notifications'];

  @service analytics!: Services['analytics'];

  @service session!: Services['session'];

  @service('repositories/team-message')
  messageRepository!: Services['repositories/team-message'];

  @tracked isSending = false;
  @tracked draft = '';
  @tracked mentions: IMentionable[] = [];
  @tracked messageToRemove?: ModelRegistry['team-message'];

  get isRemoveConfirmOpen(): boolean {
    return Boolean(this.messageToRemove);
  }

  scrollToEnd = modifier((element: HTMLElement, [messageCount]: [number]) => {
    let stickToBottom = messageCount >= 0;
    const scroll = (): void => {
      if (stickToBottom) {
        element.scrollTop = element.scrollHeight;
      }
    };
    const handleScroll = (): void => {
      const distance =
        element.scrollHeight - element.scrollTop - element.clientHeight;

      stickToBottom = distance < STICK_TO_BOTTOM_THRESHOLD;
    };
    const resizes = new ResizeObserver(scroll);
    const mutations = new MutationObserver(scroll);

    scroll();
    resizes.observe(element);
    mutations.observe(element, { childList: true, subtree: true });
    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      resizes.disconnect();
      mutations.disconnect();
      element.removeEventListener('scroll', handleScroll);
    };
  });

  get orderedMessages(): ModelRegistry['team-message'][] {
    const sentAt = (message: ModelRegistry['team-message']): number =>
      message.createdAt?.getTime() ?? Number.MAX_SAFE_INTEGER;

    return this.args.messages.slice().sort((a, b) => sentAt(a) - sentAt(b));
  }

  @action
  handleInput(value: string): void {
    this.draft = value;
  }

  @action
  handleMention(mention: IMentionable): void {
    if (!this.mentions.some((known) => known.userId === mention.userId)) {
      this.mentions = [...this.mentions, mention];
    }
  }

  @action
  async handleSubmit(): Promise<void> {
    const body = encodeMentions(this.draft.trim(), this.mentions);

    if (!body || this.isSending) {
      return;
    }

    this.isSending = true;

    const message = this.messageRepository.createRecord({
      body,
      resourceType: this.args.resourceType,
      resourceId: this.args.resourceId,
      teamConversation: this.args.conversation ?? null,
    });

    try {
      await this.messageRepository.save(message);

      this.args.onPosted(message);
      this.draft = '';
      this.analytics.trackEvent(AnalyticsEvents.teamMessagePosted, {
        resourceType: this.args.resourceType,
        mentions: message.teamMentions.length,
      });
      this.mentions = [];
    } catch {
      message.unloadRecord();
      this.notifications.error('team.thread.send_failed');
    } finally {
      this.isSending = false;
    }
  }

  @action
  handleAskRemove(message: ModelRegistry['team-message']): void {
    this.messageToRemove = message;
  }

  @action
  handleCancelRemove(): void {
    this.messageToRemove = undefined;
  }

  @action
  async handleConfirmRemove(): Promise<void> {
    const message = this.messageToRemove;

    this.messageToRemove = undefined;

    if (message) {
      await this.handleRemove(message);
    }
  }

  @action
  async handleRemove(message: ModelRegistry['team-message']): Promise<void> {
    this.isSending = true;

    try {
      await this.messageRepository.remove(message);

      this.analytics.trackEvent(AnalyticsEvents.teamMessageRemoved, {
        resourceType: this.args.resourceType,
      });
    } catch {
      this.notifications.error('team.thread.remove_failed');
    } finally {
      this.isSending = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Team::Thread': typeof UiTeamThreadComponent;
  }
}
